import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { DollarSign, Shield, Bell, Smartphone, ChevronRight, TrendingUp, Calendar, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function LandingPage() {
  const { loginWithGoogle } = useAuth();

  const features = [
    {
      icon: DollarSign,
      title: 'Safe-to-Spend',
      description: 'Know exactly how much you can spend each day without going broke'
    },
    {
      icon: Calendar,
      title: 'Bill Tracking',
      description: 'Never miss a bill. We factor them all into your daily budget'
    },
    {
      icon: TrendingUp,
      title: 'Spending Forecast',
      description: 'See your money trajectory from now until payday'
    },
    {
      icon: Bell,
      title: 'Smart Alerts',
      description: 'Get notified when you need to watch your spending'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0F1224]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo-64.png" alt="Payday Control" className="w-10 h-10 rounded-xl" />
            <span className="font-heading font-bold text-lg text-white">Payday Control</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-[#9CA3AF] hover:text-white hover:bg-white/5" data-testid="header-login-btn">
                Log in
              </Button>
            </Link>
            <Link to="/register" className="hidden sm:block">
              <Button className="bg-[#63E6E2] text-[#0F1224] hover:bg-[#4DD4CF] font-semibold" data-testid="header-signup-btn">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 relative hero-glow">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#63E6E2]/10 text-[#63E6E2] text-sm font-medium mb-6">
              Smart Money Management
            </span>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight mb-6"
          >
            Know Your
            <span className="text-[#63E6E2]"> Daily Limit</span>
            <br />Until Payday
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg sm:text-xl text-[#9CA3AF] max-w-2xl mx-auto mb-10"
          >
            Stop guessing. Payday Control calculates exactly how much you can safely spend each day after accounting for all your bills and commitments.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link to="/register">
              <Button size="lg" className="bg-[#63E6E2] text-[#0F1224] hover:bg-[#4DD4CF] font-semibold text-base px-8 h-12 btn-press" data-testid="hero-get-started-btn">
                Get Started Free
                <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline" 
              onClick={loginWithGoogle}
              className="border-[#2D3354] text-white hover:bg-white/5 font-medium text-base px-8 h-12"
              data-testid="hero-google-signin-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>
          </motion.div>
        </div>

        {/* Hero Visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="max-w-md mx-auto mt-16 relative"
        >
          <div className="bg-[#1B1F3A] border border-[#2D3354] rounded-2xl p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <span className="text-[#6B7280] text-xs uppercase tracking-wider font-medium">Safe to Spend Today</span>
              <span className="text-[#5EEAD4] text-xs font-medium">On Track</span>
            </div>
            <div className="text-center mb-6">
              <span className="font-heading text-5xl sm:text-6xl font-black text-[#63E6E2] number-display">£47.50</span>
            </div>
            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-[#2D3354]">
              <div className="text-center">
                <p className="text-[#6B7280] text-xs mb-1">Balance</p>
                <p className="text-white font-semibold">£285</p>
              </div>
              <div className="text-center">
                <p className="text-[#6B7280] text-xs mb-1">Bills Due</p>
                <p className="text-white font-semibold">£95</p>
              </div>
              <div className="text-center">
                <p className="text-[#6B7280] text-xs mb-1">Days Left</p>
                <p className="text-white font-semibold">4</p>
              </div>
            </div>
          </div>
          <div className="absolute -inset-0.5 bg-gradient-to-r from-[#63E6E2]/20 to-transparent blur-xl -z-10 rounded-3xl" />
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Everything You Need
            </h2>
            <p className="text-[#9CA3AF] text-lg max-w-2xl mx-auto">
              Simple tools to keep your spending in check between paydays
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#1B1F3A] border border-[#2D3354] rounded-xl p-6 card-hover"
              >
                <div className="w-12 h-12 rounded-lg bg-[#63E6E2]/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-[#63E6E2]" />
                </div>
                <h3 className="font-heading text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-[#9CA3AF] text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PWA Section */}
      <section className="py-20 px-4 sm:px-6 bg-[#1B1F3A]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#252A4A] border border-[#2D3354] mb-6">
              <Smartphone className="w-5 h-5 text-[#63E6E2]" />
              <span className="text-sm text-[#9CA3AF]">Works on any device</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Add to Home Screen
            </h2>
            <p className="text-[#9CA3AF] text-lg max-w-xl mx-auto mb-8">
              Install Payday Control on your phone for quick access. Works offline, sends notifications, feels like a native app.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#252A4A] border border-[#2D3354]">
                <Shield className="w-5 h-5 text-[#63E6E2]" />
                <span className="text-sm text-white">Bank-grade security</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#252A4A] border border-[#2D3354]">
                <CreditCard className="w-5 h-5 text-[#63E6E2]" />
                <span className="text-sm text-white">No card required</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Start Managing Your Money Today
            </h2>
            <p className="text-[#9CA3AF] text-lg max-w-xl mx-auto mb-8">
              Free to use. No credit card needed. Set up in 2 minutes.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-[#63E6E2] text-[#0F1224] hover:bg-[#4DD4CF] font-semibold text-base px-10 h-12 btn-press" data-testid="cta-signup-btn">
                Create Free Account
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 border-t border-[#2D3354]">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo-64.png" alt="Payday Control" className="w-8 h-8 rounded-lg" />
            <span className="font-heading font-bold text-white">Payday Control</span>
          </div>
          <p className="text-[#6B7280] text-sm">© 2024 Payday Control. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
